import React, { useContext, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import DetailPageField from './components/DetailPageField';
import DropzoneField from 'components/DropzoneField';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { Listing } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import TextareaArrayField from 'components/TextareaArrayField';
import HighlightsIcon from 'icons/Highlights.icon';
import ProductsIcon from 'icons/Products.icon';
import KeyAssetsIcon from 'icons/KeyAssets.icon';
import { Form } from 'react-final-form';
import ListingContext from '../../providers/Listing/Listing.context';
import TextAreaField from 'components/TextAreaField';
import { Button } from 'ncoded-component-library';
import { useLocation } from 'react-router-dom';
import api from 'api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import utils, {
  checkIsUrgent,
  getAcceptedListingContacts,
  getHasElementsInArray,
} from 'utils';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
  KEY_ASSETS_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
  LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
} from '../../utils';
import arrayMutators from 'final-form-arrays';
import ListingEditContext from '../../subrouters/EditListingSubrouter/providers/Listing/Listing.context';
import { SLUG_FIELD_NAME } from '../../components/ListingSlugField/ListingSlugField.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import StarIcon from 'icons/Star.icon';
import ChatIcon from 'icons/Chat.icon';
import ShareIcon from 'icons/Share.icon';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import ResolvePopup from 'components/ResolvePopup';
import EoiDate from 'components/EoiDate';
import { ReactComponent as LinkedInIcon } from 'icons/linkedin.svg';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import useLocationMapper from 'router/data/useLocationMapper';
import { EDIT_ROUTE, NEW_WINDOW_PROPS } from 'data/global';
import ListingFieldsRequiredVisibleInfoContext from '../../providers/ListingFieldsRequiredVisibleInfo/ListingFieldsRequiredVisibleInfo.context';
import PreviewPatchListingContext from '../../providers/PreviewPatchListing/PreviewPatchListing.context';
import ListingPricesPreview from './components/ListingPricesPreview';
import ListingFinancialsEditor from './components/ListingFinancialsEditor';
import ListSectionRuleNote from './components/ListSectionRuleNote';
import PreviewMissingFieldsBar from './components/PreviewMissingFieldsBar';
import PreviewMissingFieldsProvider from './providers/PreviewMissingFields';
import {
  MIN_REQUIRED_LIST_ITEMS,
  PUBLIC_LISTING_REQUIREMENT,
} from '../../data/publicListingRequirements';
import useFirstStepValues from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/components/FirstStep/hooks';

import './ListingDetailsPreview.styles.scss';
import './ListingDetailsPreview.styles.responsive.scss';

type ListingDetailsPreviewProps = {
  className?: string;
};

/** DropzoneField collapses a single file to a scalar, so both shapes turn up. */
const hasOwnImages = (gallery: Listing['gallery']) =>
  Array.isArray(gallery) ? getHasElementsInArray(gallery) : !!gallery;

const ListingDetailsPreview: React.FC<ListingDetailsPreviewProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isEdit = pathname.includes(EDIT_ROUTE);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const ListingCreateContextValues = useContext(ListingContext);
  const ListingEditContextValues = useContext(ListingEditContext);

  const {
    listingFormValues,
    listingType,
    listing: {
      _id: listingId,
      seekingExpressionOfInterestDate,
      listingContacts,
    },
    setListing,
  } = isEdit ? ListingEditContextValues : ListingCreateContextValues;

  const { fields } = useContext(ListingFieldsRequiredVisibleInfoContext);

  const {
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = fields || {};

  const {
    saveFields,
    flushNow,
    notifyUploadStart,
    notifyUploadSuccess,
    notifyUploadFailure,
  } = useContext(PreviewPatchListingContext);

  const { industrySelectOptions } = useOperationStepValues();
  const { locationLabelMapper } = useLocationMapper();

  /**
   * The going-concern location select edits dealLocation - the field the wizard
   * actually requires, and the one the published listing falls back to. The
   * precise map location stays an optional refinement, edited in form view.
   */
  const { dealLocationOptions, onLocationSearchChange } = useFirstStepValues();

  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('listing-details-preview', className);

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listingContacts),
    [listingContacts],
  );

  const uploadListingGallery: DropzoneUpload = useCallback(
    async (file, _, config, customName) => {
      notifyUploadStart();

      try {
        const { uploadedFile, listing } = await api.listings.uploadFile(
          file,
          'gallery',
          listingId,
          config,
          customName,
        );
        setListing(listing);
        notifyUploadSuccess();
        return uploadedFile;
      } catch (e) {
        notifyUploadFailure();
        popServerError(e);
      }
    },
    [
      listingId,
      notifyUploadFailure,
      notifyUploadStart,
      notifyUploadSuccess,
      popServerError,
      setListing,
    ],
  );

  const deleteListingGallery: DropzoneDelete = useCallback(
    async (file, _) => {
      notifyUploadStart();

      try {
        const {
          data: { fileId, listing },
        } = await api.listings.deleteFile(listingId, 'gallery', file.id);
        setListing(listing);
        notifyUploadSuccess();
        return fileId;
      } catch (e) {
        notifyUploadFailure();
        popServerError(e);
      }
    },
    [
      listingId,
      notifyUploadFailure,
      notifyUploadStart,
      notifyUploadSuccess,
      popServerError,
      setListing,
    ],
  );

  const uploadCompanyLogo: DropzoneUpload = useCallback(
    async (file, _, config) => {
      notifyUploadStart();

      try {
        const { uploadedFile, listing } = await api.listings.uploadFile(
          file,
          LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
          listingId,
          config,
        );
        setListing(listing);
        notifyUploadSuccess();
        return uploadedFile;
      } catch (e) {
        notifyUploadFailure();
        popServerError(e);
      }
    },
    [
      listingId,
      notifyUploadFailure,
      notifyUploadStart,
      notifyUploadSuccess,
      popServerError,
      setListing,
    ],
  );

  const deleteCompanyLogo: DropzoneDelete = useCallback(
    async (file, _) => {
      notifyUploadStart();

      try {
        const {
          data: { fileId, listing },
        } = await api.listings.deleteListingContact(
          LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
          file.id,
          listingId,
        );
        setListing(listing);
        notifyUploadSuccess();
        return fileId;
      } catch (e) {
        notifyUploadFailure();
        popServerError(e);
      }
    },
    [
      listingId,
      notifyUploadFailure,
      notifyUploadStart,
      notifyUploadSuccess,
      popServerError,
      setListing,
    ],
  );

  // Send anything still sitting in the 1s debounce before the route swaps.
  useEffect(() => () => flushNow(), [flushNow]);

  const isUrgent = checkIsUrgent(listingType);

  const imagePlaceholder = useMemo(
    () => ({ label: t('placeholderImage'), addLabel: t('addImage') }),
    [t],
  );

  // The logo is optional and has no stock image, so it shows an empty slot
  // rather than the Resolve drawing.
  const logoPlaceholder = useMemo(
    () => ({ addLabel: t('addLogo'), hideFallbackImage: true }),
    [t],
  );

  const renderFinancialsPreview = useCallback(
    () => (
      <ListingPricesPreview
        isUrgent={isUrgent}
        amountRequiredVisibleInfo={amountRequiredVisibleInfo}
        amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
        amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
        revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
        EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
        averageStockValueRequiredVisibleInfo={
          averageStockValueRequiredVisibleInfo
        }
      />
    ),
    [
      isUrgent,
      amountRequiredVisibleInfo,
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      revenueRequiredVisibleInfo,
      EBITDARequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
    ],
  );

  const renderFinancialsEditor = useCallback(
    () => (
      <ListingFinancialsEditor
        isUrgent={isUrgent}
        onSave={(patch, isValid, options) => {
          if (isValid) saveFields(patch, options);
        }}
      />
    ),
    [isUrgent, saveFields],
  );

  // Rendered as on the published listing, but inert and dimmed here.
  const ActionsPlaceholder = useMemo(
    () => (
      <div className="listing-actions listing-actions--preview" aria-hidden>
        <Button variant="outline" icon={ChatIcon} tabIndex={-1}>
          {t('enquire')}
        </Button>
        <Button variant="link" icon={StarIcon} tabIndex={-1}>
          {t('shortlist')}
        </Button>
        <Button variant="link" icon={ShareIcon} tabIndex={-1}>
          {t('share')}
        </Button>
      </div>
    ),
    [t],
  );

  return (
    <Form<Partial<Listing>>
      keepDirtyOnReinitialize
      onSubmit={utils.noop}
      initialValues={listingFormValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, values }) => {
        /**
         * The published listing prefers the precise location's state over the
         * deal location, so the preview reads the same way round.
         */
        const locationLabel = locationLabelMapper[values.location?.state];

        return (
          <form onSubmit={handleSubmit} className={classes}>
            <PreviewMissingFieldsProvider isUrgent={isUrgent}>
              <div className="form-content">
                <div>
                  {isSuperAdmin && (
                    <DetailPageField
                      name={SLUG_FIELD_NAME}
                      previewType="slug"
                      placeholder={t('listingTitleSlug')}
                    />
                  )}
                </div>
                <span className="spacing" />

                <div className="image-cont">
                  <DetailPageField
                    name="gallery"
                    previewType="images"
                    imagePlaceholder={imagePlaceholder}
                    field={{
                      component: DropzoneField,
                      uploadRequest: uploadListingGallery,
                      deleteRequest: deleteListingGallery,
                      accept: 'image/*',
                    }}
                  />
                  {!hasOwnImages(values.gallery) && (
                    <p className="image-cont__note">
                      {t('leadsSeeThisImageUntilYouAddYourOwn')}
                    </p>
                  )}
                </div>
                <div className="listing-details-preview__main">
                  <div className="title-wrapper">
                    <DetailPageField
                      name="listingTitle"
                      previewType="h1"
                      placeholder={t('addAListingTitle')}
                      saveOn="exit"
                      requirementKeys={[
                        PUBLIC_LISTING_REQUIREMENT.LISTING_TITLE,
                      ]}
                      field={{
                        component: InputField,
                        maxLength: TITLE_MAX_CHARACTERS,
                      }}
                    />
                  </div>
                  <div className="listing-details-preview__main__main-info-actions">
                    {!isUrgent ? (
                      <DetailPageField
                        name="dealLocation"
                        previewType="link"
                        placeholder={t('addALocation')}
                        label={locationLabel}
                        saveOn="exit"
                        keepClosedWhenFilled
                        requirementKeys={[PUBLIC_LISTING_REQUIREMENT.LOCATION]}
                        field={{
                          component: SelectField,
                          options: dealLocationOptions,
                          async: true,
                          onSearchChange: onLocationSearchChange,
                          renderAsPortal: true,
                          portalClassName: 'state-select-portal',
                          searchable: true,
                        }}
                      />
                    ) : (
                      <>
                        <LocationmarkerIcon />
                        <DetailPageField
                          name="locationPostcode"
                          previewType="link"
                          placeholder={t('addAPostcode')}
                          saveOn="exit"
                          requirementKeys={[
                            PUBLIC_LISTING_REQUIREMENT.LOCATION,
                          ]}
                          field={{
                            component: InputField,
                            type: 'number',
                          }}
                        />
                      </>
                    )}

                    <DetailPageField
                      name="industry"
                      previewType="link"
                      placeholder={t('addAnIndustry')}
                      saveOn="exit"
                      keepClosedWhenFilled
                      requirementKeys={[PUBLIC_LISTING_REQUIREMENT.INDUSTRY]}
                      field={{
                        component: SelectField,
                        options: industrySelectOptions,
                        renderAsPortal: true,
                        searchable: true,
                      }}
                    />
                  </div>
                  <EoiDate
                    bigger
                    seekingExpressionOfInterestDate={
                      seekingExpressionOfInterestDate
                    }
                  />
                  <DetailPageField
                    className="financials"
                    name="askingPriceSelect"
                    renderPreview={renderFinancialsPreview}
                    renderEditor={renderFinancialsEditor}
                    /**
                     * One editor covers three requirements, and the marks land on
                     * the individual tiles rather than on the block.
                     */
                    requirementKeys={[
                      PUBLIC_LISTING_REQUIREMENT.PRICE_GUIDE,
                      PUBLIC_LISTING_REQUIREMENT.REVENUE,
                      PUBLIC_LISTING_REQUIREMENT.EBITDA,
                    ]}
                    hideMissingBorder
                  />
                  <div className="listing-details-preview__main__description">
                    <DetailPageField
                      name="businessDescription"
                      previewType="p"
                      placeholder={t('addADescriptionOfTheBusiness')}
                      saveOn="debounced"
                      requirementKeys={[
                        PUBLIC_LISTING_REQUIREMENT.BUSINESS_DESCRIPTION,
                      ]}
                      field={{
                        component: TextAreaField,
                        maxLength: DESCRIPTION_MAX_CHARACTERS,
                        autoResize: true,
                      }}
                      allowEnter
                    />
                  </div>
                  {ActionsPlaceholder}
                  {/*
                   * Two of the three must hold two entries, so none is required
                   * on its own: they share one requirement, take no border, and
                   * carry the rule as a line under each list.
                   */}
                  <div className="listing-details-preview__main__highlights">
                    <div className="list-section">
                      <DetailPageField
                        name="highlights"
                        previewType="highlightlsSection"
                        saveOn="debounced"
                        requirementKeys={[
                          PUBLIC_LISTING_REQUIREMENT.LIST_SECTIONS,
                        ]}
                        hideMissingBorder
                        field={{
                          component: TextareaArrayField,
                          fieldLabel: t('highlight'),
                          maxFieldLength: HIGHLIGHTS_MAX_CHARACTERS,
                          minRequired: MIN_REQUIRED_LIST_ITEMS,
                        }}
                        icon={HighlightsIcon}
                        placeholder={t('addAHighlight')}
                      />
                      <ListSectionRuleNote />
                    </div>
                    <div className="list-section">
                      <DetailPageField
                        name="products"
                        previewType="highlightlsSection"
                        saveOn="debounced"
                        requirementKeys={[
                          PUBLIC_LISTING_REQUIREMENT.LIST_SECTIONS,
                        ]}
                        hideMissingBorder
                        field={{
                          component: TextareaArrayField,
                          fieldLabel: t('product'),
                          maxFieldLength: PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
                          minRequired: MIN_REQUIRED_LIST_ITEMS,
                        }}
                        icon={ProductsIcon}
                        placeholder={t('addAProduct')}
                      />
                      <ListSectionRuleNote />
                    </div>
                    <div className="list-section">
                      <DetailPageField
                        name="keyAssets"
                        previewType="highlightlsSection"
                        saveOn="debounced"
                        requirementKeys={[
                          PUBLIC_LISTING_REQUIREMENT.LIST_SECTIONS,
                        ]}
                        hideMissingBorder
                        field={{
                          component: TextareaArrayField,
                          fieldLabel: t('keyAsset'),
                          maxFieldLength: KEY_ASSETS_MAX_CHARACTERS,
                          minRequired: MIN_REQUIRED_LIST_ITEMS,
                        }}
                        icon={KeyAssetsIcon}
                        placeholder={t('addAKeyAsset')}
                      />
                      <ListSectionRuleNote />
                    </div>
                  </div>
                </div>
                <aside>
                  <div className="resolve-agent-details">
                    <p className="title">{t('presentedBy')}</p>
                    <DetailPageField
                      name={'listingContact.firmLogo' as keyof Listing}
                      previewType="images"
                      imagePlaceholder={logoPlaceholder}
                      field={{
                        component: DropzoneField,
                        maxFiles: 1,
                        uploadRequest: uploadCompanyLogo,
                        deleteRequest: deleteCompanyLogo,
                        accept: 'image/*',
                      }}
                    />
                    {getHasElementsInArray(acceptedListingContacts) && (
                      <ul>
                        {acceptedListingContacts.map(
                          ({
                            email,
                            name,
                            position,
                            linkedinProfile,
                            brokerImage,
                          }) => (
                            <li key={email}>
                              <div className="res-lc-content">
                                <span>{name}</span>
                                <span>{position}</span>
                                {linkedinProfile ? (
                                  <a
                                    href={linkedinProfile}
                                    {...NEW_WINDOW_PROPS}
                                  >
                                    <LinkedInIcon />
                                    {t('linkedInProfile')}
                                  </a>
                                ) : (
                                  <span />
                                )}
                              </div>
                              <ResolveImage
                                src={getImgUrl(
                                  brokerImage?.url,
                                  RESIZED_WIDTH[240],
                                )}
                              />
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                    {getHasElementsInArray(acceptedListingContacts) && (
                      <p className="resolve-agent-details__profile-note">
                        <ResolvePopup
                          onHover
                          popupContent={
                            <span>
                              {t('nameRoleAndPhotoComeFromYourProfile')}
                            </span>
                          }
                        >
                          <LockclosedIcon />
                        </ResolvePopup>
                        {t('fromYourProfile')}
                      </p>
                    )}
                    {ActionsPlaceholder}
                    <p className="resolve-agent-details__actions-note">
                      {t('leadActionsNotActiveInPreview')}
                    </p>
                  </div>
                </aside>
              </div>
              <PreviewMissingFieldsBar />
            </PreviewMissingFieldsProvider>
          </form>
        );
      }}
    />
  );
};

export default ListingDetailsPreview;
